import React from 'react';
import { View } from 'react-native';

import { Text } from '@/components/ui/text';
import type { useToast } from '../../../context/ToastContext';
import { trackAuthEvent } from '../../../services/analytics';
import { userProfileAPI } from '../../../services/api';
import { showDialog } from '../../../utils/dialogs';
import { buildExportBlob, buildExportFilename, downloadBlob } from '../dataExport';
import { PRIVACY_URL, openExternalLink } from '../profileLinks';
import { profileWebStyles } from '../profileStyles';
import type { Translate } from '../profileTypes';
import { ProfileActionRow } from './ProfileActionRow';

interface PrivacyDataPanelProps {
  onDeleteAccount: () => void;
  showToast: ReturnType<typeof useToast>['showToast'];
  /** Stacked in a single column (phone/tablet). */
  isCompact: boolean;
  t: Translate;
}

/** "Privacy & data" panel: data export, privacy policy, delete account. */
export function PrivacyDataPanel({ onDeleteAccount, showToast, isCompact, t }: PrivacyDataPanelProps) {
  const handleExportData = async () => {
    try {
      const response = await userProfileAPI.exportData();
      const filename = buildExportFilename(response.exported_at);
      downloadBlob(buildExportBlob(response.export_data), filename);
      trackAuthEvent('data_export_completed', { exported_at: response.exported_at });
      showToast(t('profile.dataExportDownloaded', { filename }), 'success');
    } catch {
      trackAuthEvent('data_export_failed');
      showDialog(t('profile.exportFailed'));
    }
  };

  return (
    <View style={[profileWebStyles.panel, isCompact && profileWebStyles.panelStacked]}>
      <Text style={profileWebStyles.panelTitle}>{t('profile.webPrivacyData')}</Text>
      <ProfileActionRow
        title={t('profile.webExportDataTitle')}
        subtitle={t('profile.webExportDataSubtitle')}
        icon="download"
        onPress={handleExportData}
      />
      <ProfileActionRow
        title={t('profile.webPrivacyPolicyTitle')}
        subtitle={t('profile.webPrivacyPolicySubtitle')}
        icon="privacy-tip"
        onPress={() => openExternalLink(t, PRIVACY_URL, t('profile.legalLinkFailed'))}
      />
      <ProfileActionRow
        title={t('profile.webDeleteAccountTitle')}
        subtitle={t('profile.webDeleteAccountSubtitle')}
        icon="delete-outline"
        onPress={onDeleteAccount}
        dangerous
      />
    </View>
  );
}
